/**
 * Unified API Configuration for DataHub Frontend
 * Centralized configuration for all API calls
 */

import axios from 'axios';
import { store } from '../store';

// Environment detection
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

// API Configuration
const API_CONFIG = {
    baseURL: process.env.REACT_APP_API_URL || (isDevelopment ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1'),
    timeout: 30000, // 30 seconds timeout
    headers: {
        'Content-Type': 'application/json'
    }
};

// Create axios instance
export const apiClient = axios.create(API_CONFIG);

// Import unified auth manager
import { authManager } from '../utils/authManager';

// Request interceptor
apiClient.interceptors.request.use(
    (config) => {
        // Add authentication token
        const token = authManager.getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Add request timestamp for debugging
        config.metadata = { startTime: new Date().getTime() };

        // Log request in development
        if (isDevelopment) {
            console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
                headers: config.headers,
                data: config.data,
                params: config.params
            });
        }

        return config;
    },
    (error) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor
apiClient.interceptors.response.use(
    (response) => {
        // Calculate request duration
        const duration = new Date().getTime() - response.config.metadata.startTime;

        // Log response in development
        if (isDevelopment) {
            console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
                status: response.status,
                duration: `${duration}ms`,
                data: response.data
            });
        }

        return response;
    },
    (error) => {
        const duration = error.config?.metadata ? new Date().getTime() - error.config.metadata.startTime : 0;

        // Enhanced error logging
        if (error.response) {
            // Server responded with error status
            console.error(`❌ API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
                status: error.response.status,
                statusText: error.response.statusText,
                duration: `${duration}ms`,
                data: error.response.data,
                headers: error.response.headers
            });

            // Handle specific error cases
            switch (error.response.status) {
                case 401:
                    console.warn('🔐 Unauthorized access - clearing tokens');
                    authManager.clearTokens();
                    // Dispatch logout action if store is available
                    if (store && store.dispatch) {
                        store.dispatch({ type: 'auth/logout' });
                    }
                    break;

                case 403:
                    console.warn('🚫 Insufficient permissions');
                    break;

                case 404:
                    console.warn('🔍 Resource not found');
                    break;

                case 422:
                    console.warn('📝 Validation error:', error.response.data);
                    break;

                case 500:
                    console.error('🔥 Server error occurred');
                    break;

                default:
                    console.error('❓ Unexpected error:', error.response.status);
            }
        } else if (error.request) {
            // Network error
            console.error('🌐 Network Error:', {
                url: error.config?.url,
                method: error.config?.method,
                duration: `${duration}ms`,
                message: error.message
            });
        } else {
            // Other error
            console.error('💥 Request Setup Error:', error.message);
        }

        return Promise.reject(error);
    }
);

// API Service Classes
export class BaseApiService {
    constructor(endpoint = '') {
        this.endpoint = endpoint;
    }

    async get(path = '', params = {}) {
        const response = await apiClient.get(`${this.endpoint}${path}`, { params });
        return response.data;
    }

    async post(path = '', data = {}) {
        const response = await apiClient.post(`${this.endpoint}${path}`, data);
        return response.data;
    }

    async put(path = '', data = {}) {
        const response = await apiClient.put(`${this.endpoint}${path}`, data);
        return response.data;
    }

    async delete(path = '') {
        const response = await apiClient.delete(`${this.endpoint}${path}`);
        return response.data;
    }

    async patch(path = '', data = {}) {
        const response = await apiClient.patch(`${this.endpoint}${path}`, data);
        return response.data;
    }
}

// Specific API Services
export class ProjectsApiService extends BaseApiService {
    constructor() {
        super('/projects');
    }

    async getProjects(userToken) {
        return this.get('', { user_token: userToken });
    }

    async createProject(projectData) {
        return this.post('', projectData);
    }

    async getProject(projectName, userToken) {
        return this.get(`/${projectName}`, { user_token: userToken });
    }

    async updateProject(projectName, projectData) {
        return this.put(`/${projectName}`, projectData);
    }

    /**
     * @deprecated Use RTK Query apiSlice.endpoints.deleteProject (Bearer auth) instead.
     * DELETE project đã chuẩn hóa qua store/slices/apiSlice.js.
     */
    async deleteProject(projectName, userToken) {
        return this.delete(`/${projectName}?user_token=${userToken}`);
    }
}

export class JupyterHubApiService extends BaseApiService {
    constructor() {
        super('/jupyterhub');
    }

    async getEnvironments(userToken, projectName) {
        return this.get('/environments', { user_token: userToken, project_name: projectName });
    }

    async createEnvironment(environmentData) {
        return this.post('/environments', environmentData);
    }

    async getServers(userToken, projectName) {
        return this.get('/servers', { user_token: userToken, project_name: projectName });
    }

    async startServer(serverId) {
        return this.post(`/servers/${serverId}/start`);
    }

    async stopServer(serverId) {
        return this.post(`/servers/${serverId}/stop`);
    }

    async deleteServer(serverId) {
        return this.delete(`/servers/${serverId}`);
    }
}

export class RBACApiService extends BaseApiService {
    constructor() {
        super('/rbac');
    }

    async getPermissions(category = null) {
        return this.get('/permissions', category ? { category } : {});
    }

    async createPermission(permission) {
        return this.post('/permissions', permission);
    }

    async getRoles(organizationId = null) {
        return this.get('/roles', organizationId ? { organization_id: organizationId } : {});
    }

    async createRole(role) {
        return this.post('/roles', role);
    }

    async getUsers(organizationId = null) {
        return this.get('/users', organizationId ? { organization_id: organizationId } : {});
    }

    async checkPermission(userId, resource, action, organizationId = null) {
        return this.post('/check-permission', {
            user_id: userId,
            resource,
            action,
            organization_id: organizationId
        });
    }
}

// Export service instances
export const projectsApi = new ProjectsApiService();
export const jupyterHubApi = new JupyterHubApiService();
export const rbacApi = new RBACApiService();

// Export configuration
export const apiConfig = {
    baseURL: API_CONFIG.baseURL,
    timeout: API_CONFIG.timeout,
    isDevelopment,
    isProduction
};

// Health check utility
export const healthCheck = async () => {
    try {
        const response = await apiClient.get('/health/check');
        return { status: 'healthy', data: response.data };
    } catch (error) {
        return { status: 'unhealthy', error: error.message };
    }
};

export default apiClient;
